import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { X, Info, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useState, useRef } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { RequiredMark } from "./required-mark";

export interface Option {
  label: string;
  value: string;
  color?: string;
  [key: string]: any; // Allow additional properties
}

export interface SelectV2Props {
  value?: string;
  onChange?: (value: string) => void;
  options: Option[];
  label?: string;
  topLabel?: string;
  placeholder?: string;
  clearable?: boolean;
  wrapperClassName?: string;
  contentClassName?: string;
  triggerClassName?: string;
  rightElement?: React.ReactNode;
  tooltip?: string;
  isDisabled?: boolean;
  error?: string;
  id?: string;
  renderOption?: (option: Option) => React.ReactNode;
  renderValue?: (option: Option | undefined) => React.ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
  required?: boolean;
}

export const SelectV2 = ({
  value,
  onChange,
  options,
  label, // label inside the select trigger
  topLabel, // label on top of the select trigger
  placeholder = "",
  clearable = false,
  isDisabled = false,
  searchable = false,
  searchPlaceholder,
  wrapperClassName, // wrapper class name
  contentClassName, // content class name
  triggerClassName, // select trigger class name
  rightElement,
  tooltip,
  error,
  id,
  renderOption,
  renderValue,
  required = false,
}: SelectV2Props) => {
  const { t } = useTranslation(["shared", "common"]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const lastInputInteractionRef = useRef(0);
  const isOpenRef = useRef(false);

  const normalizeString = (str: string) =>
    str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const filteredOptions = searchable
    ? options.filter((option) =>
        normalizeString(option.label).includes(normalizeString(searchQuery)),
      )
    : options;

  const closeSelect = () => {
    isOpenRef.current = false;
    setIsOpen(false);
    setSearchQuery("");
  };

  // Controlled open/close: ignore spurious close events that Samsung tablets
  // fire when the search input receives focus or is tapped.
  const handleOpenChange = (open: boolean) => {
    if (!open && searchable && Date.now() - lastInputInteractionRef.current < 400) {
      return;
    }
    isOpenRef.current = open;
    setIsOpen(open);
    if (!open) {
      setSearchQuery("");
    }
  };

  // Callback ref: auto-focus the search input when it mounts into the DOM
  // (i.e. when the select content portal renders).
  const searchInputCallbackRef = useCallback((node: HTMLInputElement | null) => {
    searchInputRef.current = node;
    if (node) {
      lastInputInteractionRef.current = Date.now();
      requestAnimationFrame(() => {
        node.focus({ preventScroll: true });
      });
    }
  }, []);

  // Cleanup: remove pointer-events block if component unmounts while select is open
  useEffect(() => {
    return () => {
      setTimeout(() => {
        document.body.style.pointerEvents = "";
      }, 0);
    };
  }, []);

  const handleValueChange = (newValue: string) => {
    onChange?.(newValue);
    closeSelect();
  };

  const handleClear = () => {
    onChange?.("");
  };

  // Radix useControllableState treats `value === undefined` as uncontrolled. Passing `undefined`
  // after reset (or from optional filter fields) flips controlled → uncontrolled and leaves a
  // stale label. Always pass a string; "" maps to placeholder per Radix shouldShowPlaceholder.
  const rootValue =
    value !== undefined && value !== null && String(value).length > 0 ? String(value) : "";
  const hasSelection = rootValue.length > 0;
  const selectedOption = hasSelection
    ? options.find((option) => option.value === rootValue)
    : undefined;

  return (
    <div className={cn("flex flex-col gap-2", wrapperClassName)}>
      {topLabel && (
        <Label htmlFor={id}>
          {topLabel}
          {required && !label && <RequiredMark />}
        </Label>
      )}
      <div
        className={cn(
          "relative flex w-full items-center rounded-lg border bg-white px-0 transition-all duration-200",
          label && "h-14",
          {
            "border-blue-600": isOpen && !selectedOption?.color,
            "border-red-500": error,
            "border-gray-300": !isOpen && !error && !selectedOption?.color,
            "bg-gray-50": isDisabled,
            "pointer-events-none": isDisabled,
            "hover:border-gray-400": !isDisabled && !isOpen && !error && !selectedOption?.color,
          },
          contentClassName,
        )}
        style={
          selectedOption?.color
            ? {
                borderColor: selectedOption.color,
                backgroundColor: `${selectedOption.color}1a`,
                color: selectedOption.color,
              }
            : undefined
        }
      >
        {label && (
          <Label
            className={cn(
              "pointer-events-none absolute px-4 text-sm text-gray-500 transition-all duration-200 ease-in-out",
              {
                "-translate-y-3 text-xs": hasSelection,
                "translate-y-0 text-sm": !hasSelection,
              },
            )}
            htmlFor={id}
          >
            {label}
            {required && <RequiredMark />}
          </Label>
        )}

        <Select
          value={rootValue}
          open={isOpen}
          onValueChange={handleValueChange}
          disabled={isDisabled}
          onOpenChange={handleOpenChange}
        >
          <SelectTrigger
            id={id}
            className={cn(
              "flex h-[100%] w-full min-w-0 border-0 bg-transparent p-0 px-4 shadow-none focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:outline-hidden *:data-[slot=select-value]:!block *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:truncate",
              {
                "translate-y-1.5": label && hasSelection,
              },
              triggerClassName,
            )}
          >
            {renderValue && selectedOption ? (
              <SelectValue placeholder={placeholder}>{renderValue(selectedOption)}</SelectValue>
            ) : (
              <SelectValue placeholder={placeholder} />
            )}
          </SelectTrigger>
          <SelectContent>
            {searchable && (
              <div className="sticky -top-1 z-10 flex items-center border-b bg-white px-3 pb-2">
                <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                <input
                  ref={searchInputCallbackRef}
                  type="text"
                  placeholder={searchPlaceholder ?? t("selectV2.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="placeholder:text-muted-foreground flex h-8 w-full rounded-md bg-transparent text-sm outline-none"
                  onPointerDown={() => {
                    lastInputInteractionRef.current = Date.now();
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      if (isOpenRef.current && searchInputRef.current) {
                        lastInputInteractionRef.current = Date.now();
                        searchInputRef.current.focus({ preventScroll: true });
                      }
                    }, 0);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      closeSelect();
                    }
                    e.stopPropagation();
                  }}
                />
              </div>
            )}
            <SelectGroup>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    preventFocusOnHover={searchable}
                  >
                    {renderOption ? renderOption(option) : option.label}
                  </SelectItem>
                ))
              ) : (
                <div className="text-muted-foreground py-6 text-center text-sm">
                  {t("common:states.noResults")}
                </div>
              )}
            </SelectGroup>
          </SelectContent>
        </Select>

        {rightElement && <div className="ml-2 flex flex-row items-center">{rightElement}</div>}

        {tooltip && (
          <div className="has-tooltip ml-2 self-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="size-5 text-blue-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-64 text-sm">{tooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}

        {clearable && hasSelection && !isDisabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "absolute top-1/2 right-8 h-4 w-4 -translate-y-1/2 p-0 hover:bg-transparent",
              label && "top-3/5",
            )}
            onClick={handleClear}
          >
            <X className="h-3 w-3" />
            <span className="sr-only">{t("selectV2.clear")}</span>
          </Button>
        )}
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};
